import React, { useCallback, useEffect, useState } from 'react';
import { mediaApi } from '../../services/api';
import { MediaAssetDto, MediaOwnerUsageDto } from '../../types/Media';

// Matches MediaScopes on the server. 'assets' is the herengaanuku mirror,
// which is a byte copy with no rows in the index — it will always look empty
// here, and that is correct rather than a bug to chase.
const SCOPES = ['partners', 'volunteer', 'messages', 'avatars', 'assets'];

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Browsing and removing stored media.
 *
 * The screen that answers "what does this partner have" and "remove everything
 * for this account" — questions nothing could answer before there was an index
 * to ask. Deleting here removes the blob immediately, because the container is
 * public and a row marked deleted would leave the image fetchable by anyone
 * holding its URL.
 */
const MediaPage: React.FC = () => {
  const [scope, setScope] = useState('partners');
  const [usage, setUsage] = useState<MediaOwnerUsageDto[]>([]);
  const [selectedOwner, setSelectedOwner] = useState<string | null>(null);
  const [assets, setAssets] = useState<MediaAssetDto[]>([]);
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const loadUsage = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setUsage(await mediaApi.usage(scope));
    } catch {
      setError('Failed to load media usage.');
    } finally {
      setLoading(false);
    }
  }, [scope]);

  const loadAssets = useCallback(async (ownerId: string) => {
    setError('');
    try {
      const result = await mediaApi.search({ scope, ownerId, includeDeleted, pageSize: 100 });
      setAssets(result.items);
    } catch {
      setError('Failed to load that owner’s media.');
    }
  }, [scope, includeDeleted]);

  useEffect(() => { void loadUsage(); }, [loadUsage]);

  useEffect(() => {
    if (selectedOwner) void loadAssets(selectedOwner);
  }, [selectedOwner, loadAssets]);

  const removeOne = async (asset: MediaAssetDto) => {
    if (!window.confirm(
      `Delete this image?\n\nThe file is removed immediately and cannot be recovered.`)) return;
    setBusy(true);
    try {
      await mediaApi.remove(asset.id);
      if (selectedOwner) await loadAssets(selectedOwner);
      await loadUsage();
    } catch {
      setError('Could not delete that image.');
    } finally {
      setBusy(false);
    }
  };

  const removeOwner = async (ownerId: string, count: number) => {
    // Typed confirmation rather than an OK button: this is the irreversible
    // one, and an owner id is not something you delete by reflex.
    const typed = window.prompt(
      `Delete all ${count} images for this account?\n\n`
      + `The files are removed immediately and cannot be recovered.\n`
      + `Type the account id to confirm:\n\n${ownerId}`);
    if (typed !== ownerId) return;

    setBusy(true);
    try {
      const result = await mediaApi.removeForOwner(ownerId, scope);
      // Report what actually happened rather than assuming it all worked.
      if (result.failed > 0) {
        setError(`Deleted ${result.deleted}, but ${result.failed} could not be removed. Try again.`);
      }
      setSelectedOwner(null);
      setAssets([]);
      await loadUsage();
    } catch {
      setError('Could not delete that account’s media.');
    } finally {
      setBusy(false);
    }
  };

  const ownerTotal = usage.find((u) => u.ownerId === selectedOwner)?.count ?? assets.length;

  const totalBytes = usage.reduce((sum, u) => sum + u.bytes, 0);
  const totalCount = usage.reduce((sum, u) => sum + u.count, 0);

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Media</h1>
        <p className="mt-1 text-sm text-gray-600">
          Images stored for listings, events and messages. Deleting removes the
          file straight away — the container is public, so anything still there
          is readable by anyone holding its address.
        </p>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <label className="text-sm font-medium text-gray-700">
          Scope{' '}
          <select
            value={scope}
            onChange={(e) => { setScope(e.target.value); setSelectedOwner(null); setAssets([]); }}
            className="ml-1 rounded-lg border border-gray-300 px-3 py-1.5 text-sm"
          >
            {SCOPES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>

        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={includeDeleted}
            onChange={(e) => setIncludeDeleted(e.target.checked)}
          />
          Show deleted
        </label>

        {!loading && (
          <span className="text-sm text-gray-500">
            {usage.length} account{usage.length === 1 ? '' : 's'} · {totalCount} image
            {totalCount === 1 ? '' : 's'} · {formatBytes(totalBytes)}
          </span>
        )}
      </div>

      {error && (
        <div className="mb-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-800">{error}</div>
      )}

      {loading ? (
        <div className="py-12 text-center text-gray-500">Loading…</div>
      ) : usage.length === 0 ? (
        <div className="rounded-lg border border-gray-200 py-12 text-center text-gray-500">
          Nothing stored in this scope yet.
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="lg:col-span-1">
            <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-gray-500">
              Accounts
            </h2>
            <ul className="divide-y divide-gray-200 rounded-lg border border-gray-200">
              {usage.map((u) => (
                <li key={u.ownerId}>
                  <button
                    type="button"
                    onClick={() => setSelectedOwner(u.ownerId)}
                    className={`w-full px-4 py-3 text-left hover:bg-gray-50 ${
                      selectedOwner === u.ownerId ? 'bg-indigo-50' : ''
                    }`}
                  >
                    <span className="block truncate font-mono text-xs text-gray-900">
                      {u.ownerId}
                    </span>
                    <span className="mt-0.5 block text-xs text-gray-500">
                      {u.count} image{u.count === 1 ? '' : 's'} · {formatBytes(u.bytes)}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-2">
            {!selectedOwner ? (
              <div className="rounded-lg border border-dashed border-gray-300 py-12 text-center text-gray-500">
                Pick an account to see what it holds.
              </div>
            ) : (
              <>
                <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                  <h2 className="font-mono text-xs text-gray-900">{selectedOwner}</h2>
                  <button
                    type="button"
                    disabled={busy || assets.length === 0}
                    // The owner's true total, not the page of 100 on screen.
                    // Confirming "delete all 100" and losing 340 is the kind of
                    // surprise this prompt exists to prevent.
                    onClick={() => removeOwner(selectedOwner, ownerTotal)}
                    className="rounded-lg bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
                  >
                    Delete everything for this account
                  </button>
                </div>

                {assets.length < ownerTotal && (
                  <p className="mb-3 text-xs text-gray-500">
                    Showing {assets.length} of {ownerTotal}. Deleting the account
                    removes all of them.
                  </p>
                )}

                {assets.length === 0 ? (
                  <div className="rounded-lg border border-gray-200 py-12 text-center text-gray-500">
                    Nothing stored for this account.
                  </div>
                ) : (
                  <ul className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                    {assets.map((a) => (
                      <li
                        key={a.id}
                        className={`overflow-hidden rounded-lg border ${
                          a.isDeleted ? 'border-gray-200 opacity-60' : 'border-gray-200'
                        }`}
                      >
                        <div className="relative aspect-[4/3] bg-gray-100">
                          {!a.isDeleted && (
                            <img src={a.url} alt="" className="h-full w-full object-cover" />
                          )}
                          {a.isDeleted && (
                            <div className="flex h-full w-full items-center justify-center text-xs text-gray-500">
                              Deleted
                            </div>
                          )}
                          <span className="absolute left-1.5 top-1.5 rounded bg-black/70 px-1.5 py-0.5 text-[10px] uppercase text-white">
                            {a.role}
                          </span>
                        </div>
                        <div className="space-y-1 p-2">
                          {/* React escapes this; it is the uploader's filename. */}
                          <p className="truncate text-xs text-gray-700" title={a.originalFileName ?? ''}>
                            {a.originalFileName ?? 'unnamed'}
                          </p>
                          <p className="text-[11px] text-gray-500">
                            {a.width}×{a.height} · {formatBytes(a.bytes)}
                          </p>
                          <p className="text-[11px] text-gray-500">
                            {new Date(a.uploadedAt).toLocaleDateString('en-NZ')}
                            {a.isDeleted && a.deletedBy && ` · removed by ${a.deletedBy}`}
                          </p>
                          {!a.isDeleted && (
                            <button
                              type="button"
                              disabled={busy}
                              onClick={() => removeOne(a)}
                              className="text-xs font-medium text-red-600 hover:underline disabled:opacity-50"
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MediaPage;
